import { MenuItem, MenuSeparator, MenuSub } from '@affine/component';
import { Button } from '@affine/component/ui/button';
import { FeatureFlagService } from '@affine/core/modules/feature-flag';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import {
  ExportIcon,
  ExportToHtmlIcon,
  ExportToMarkdownIcon,
  ExportToPngIcon,
  PageIcon,
  PrinterIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import type { ReactElement, SVGAttributes } from 'react';
import { useCallback } from 'react';

import { transitionStyle } from './index.css';

interface ExportMenuItemProps<T> {
  onSelect: () => void;
  className?: string;
  type: T;
  icon: ReactElement<SVGAttributes<SVGElement>>;
  label: string;
  keepMenuOpenOnSelect?: boolean;
  standalone?: boolean;
}

interface ExportProps {
  exportHandler: (
    type: 'pdf' | 'html' | 'png' | 'markdown' | 'snapshot' | 'pdf-export'
  ) => void;
  pageMode?: 'page' | 'edgeless';
  className?: string;
  keepMenuOpenOnSelect?: boolean;
  standalone?: boolean;
}

export function ExportMenuItem<T>({
  onSelect,
  className,
  type,
  icon,
  label,
  keepMenuOpenOnSelect,
  standalone,
}: ExportMenuItemProps<T>) {
  if (standalone) {
    return (
      <Button
        className={className}
        data-testid={`export-to-${type}`}
        onClick={onSelect}
        block
        prefix={icon}
        variant="plain"
      >
        {label}
      </Button>
    );
  }

  return (
    <MenuItem
      className={className}
      data-testid={`export-to-${type}`}
      onSelect={event => {
        if (keepMenuOpenOnSelect) {
          event.preventDefault();
        }
        onSelect();
      }}
      block
      prefixIcon={icon}
    >
      {label}
    </MenuItem>
  );
}

export const PrintMenuItems = ({
  exportHandler,
  className = transitionStyle,
  keepMenuOpenOnSelect,
  standalone,
}: ExportProps) => {
  const t = useI18n();
  return (
    <ExportMenuItem
      onSelect={() => exportHandler('pdf')}
      className={className}
      type="pdf"
      icon={<PrinterIcon />}
      label={t['com.streamlain.export.print']()}
      keepMenuOpenOnSelect={keepMenuOpenOnSelect}
      standalone={standalone}
    />
  );
};

export const ExportMenuItems = ({
  exportHandler,
  className = transitionStyle,
  pageMode = 'page',
  keepMenuOpenOnSelect,
  standalone,
}: ExportProps) => {
  const t = useI18n();
  const featureFlags = useService(FeatureFlagService).flags;
  const enable_pdfmake_export = useLiveData(
    featureFlags.enable_pdfmake_export.$
  );
  const isPageMode = pageMode !== 'edgeless';

  return (
    <>
      {isPageMode ? (
        <ExportMenuItem
          onSelect={() => exportHandler('html')}
          className={className}
          type="html"
          icon={<ExportToHtmlIcon />}
          label={t['Export to HTML']()}
          keepMenuOpenOnSelect={keepMenuOpenOnSelect}
          standalone={standalone}
        />
      ) : null}
      <ExportMenuItem
        onSelect={() => exportHandler('png')}
        className={className}
        type="png"
        icon={<ExportToPngIcon />}
        label={t['Export to PNG']()}
        keepMenuOpenOnSelect={keepMenuOpenOnSelect}
        standalone={standalone}
      />
      {isPageMode ? (
        <ExportMenuItem
          onSelect={() => exportHandler('markdown')}
          className={className}
          type="markdown"
          icon={<ExportToMarkdownIcon />}
          label={t['Export to Markdown']()}
          keepMenuOpenOnSelect={keepMenuOpenOnSelect}
          standalone={standalone}
        />
      ) : null}
      {isPageMode && enable_pdfmake_export && (
        <ExportMenuItem
          onSelect={() => exportHandler('pdf-export')}
          className={className}
          type="pdf-export"
          icon={<PrinterIcon />}
          label={t['Export to PDF']()}
          keepMenuOpenOnSelect={keepMenuOpenOnSelect}
          standalone={standalone}
        />
      )}
      <ExportMenuItem
        onSelect={() => exportHandler('snapshot')}
        className={className}
        type="snapshot"
        icon={<PageIcon />}
        label={t['Export to Snapshot']()}
        keepMenuOpenOnSelect={keepMenuOpenOnSelect}
        standalone={standalone}
      />
    </>
  );
};

export const Export = ({
  exportHandler,
  className,
  pageMode,
  keepMenuOpenOnSelect,
}: ExportProps) => {
  const t = useI18n();
  const items = (
    <>
      <ExportMenuItems
        exportHandler={exportHandler}
        className={className}
        pageMode={pageMode}
        keepMenuOpenOnSelect={keepMenuOpenOnSelect}
      />
      {pageMode !== 'edgeless' && (
        <>
          <MenuSeparator />
          <PrintMenuItems
            exportHandler={exportHandler}
            className={className}
            keepMenuOpenOnSelect={keepMenuOpenOnSelect}
          />
        </>
      )}
    </>
  );
  const handleExportMenuOpenChange = useCallback((open: boolean) => {
    if (open) {
      track.$.header.docOptions.export();
    }
  }, []);

  return (
    <MenuSub
      items={items}
      triggerOptions={{
        className: transitionStyle,
        prefixIcon: <ExportIcon />,
        'data-testid': 'export-menu',
      }}
      subOptions={{
        onOpenChange: handleExportMenuOpenChange,
      }}
    >
      {t.Export()}
    </MenuSub>
  );
};
